import {ref} from 'vue'
import {defineStore} from 'pinia'
import {Mode, type CardsDeckProgress, type CardsModuleProgress, type StudyMode} from '@shared/types'
import {clientApi} from '@/api'

type MaterialProgress = {completed: number; total: number; percent: number}

type ModeRecord<T> = Record<StudyMode, T>

function emptyFsrsProgress(): CardsDeckProgress {
  return {
    n: 0,
    m: 0,
    percent: 0,
    breakdown: {new: 0, learning: 0, review: 0, relearning: 0},
  }
}

function emptyMaterialProgress(): MaterialProgress {
  return {completed: 0, total: 0, percent: 0}
}

export const useLessonProgressStore = defineStore('lessonProgress', () => {
  const fsrsProgress = ref<ModeRecord<CardsDeckProgress>>({
    listen: emptyFsrsProgress(),
    translate: emptyFsrsProgress(),
  })
  const materialProgress = ref<ModeRecord<MaterialProgress>>({
    listen: emptyMaterialProgress(),
    translate: emptyMaterialProgress(),
  })
  const modules = ref<CardsModuleProgress[]>([])
  const indexUnits = ref<Array<{level: number; unit: number}>>([])

  async function load(profileId: number, targetLanguage: string, nativeLanguage: string): Promise<void> {
    const [index, listenMaterialProgress, translateMaterialProgress, listenDeck, translateDeck, moduleProgress] =
      await Promise.all([
        clientApi.materials.getMaterialIndex(targetLanguage),
        clientApi.materials.getTotalProgress(Mode.Listen, profileId),
        clientApi.materials.getTotalProgress(Mode.Translate, profileId),
        clientApi.cards.getDeck({profile_id: profileId, targetLanguage, nativeLanguage, mode: Mode.Listen, limit: 1}),
        clientApi.cards.getDeck({
          profile_id: profileId,
          targetLanguage,
          nativeLanguage,
          mode: Mode.Translate,
          limit: 1,
        }),
        clientApi.cards.getModuleProgress(profileId, targetLanguage, nativeLanguage),
      ])

    fsrsProgress.value = {
      listen: listenDeck.progress,
      translate: translateDeck.progress,
    }
    modules.value = moduleProgress.modules
    indexUnits.value = (index.units as any) || []

    const total = index.levels.reduce((sum, level) => sum + level.slides_count, 0)
    const materialProgressFor = (items: typeof listenMaterialProgress): MaterialProgress => {
      const completed = items.reduce((sum, item) => sum + item.completed, 0)
      return {completed, total, percent: total > 0 ? (completed / total) * 100 : 0}
    }
    materialProgress.value = {
      listen: materialProgressFor(listenMaterialProgress),
      translate: materialProgressFor(translateMaterialProgress),
    }
  }

  function moduleProgressFor(level: number, unit: number): CardsModuleProgress | null {
    return modules.value.find((item) => item.level === level && item.unit === unit) ?? null
  }

  return {fsrsProgress, materialProgress, modules, indexUnits, load, moduleProgressFor}
})
